// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import translate from "@insite/client-framework/Translate";
import { FormField, FormState } from "@stanion/XDL/Utilities/form";
import { maxLengthAttr } from '@stanion/XDL/Utilities/validation';

export interface AccountFormState extends FormState {
    fields: {
        firstName: FormField<string>;
        lastName: FormField<string>;
        email: FormField<string>;
        userName: FormField<string>;
        phone: FormField<string>;
        password: FormField<string>;
        confirmPassword: FormField<string>;
        companyName: FormField<string>;
        address1: FormField<string>;
        address2: FormField<string>;
        country: FormField<string>;
        city: FormField<string>;
        state: FormField<string>;
        zip: FormField<string>;
        accountNumber: FormField<string>;
        notes: FormField<string>;
        creditApplicationFile: FormField<any>;
    };
}

export const initialAccountFormState: AccountFormState = {
    isValid: false,
    isSubmitted: false,
    fields: {
        firstName: {
            value: "",
            validators: ["required"],
            attrs: {
                id: "createAccount_firstName",
                name: "firstName",
                label: translate("First Name"),
                placeholder: translate("Your First Name"),
                required: true,
                maxLength: 40,
            },
        },
        lastName: {
            value: "",
            validators: ["required"],
            attrs: {
                id: "createAccount_lastName",
                name: "lastName",
                label: translate("Last Name"),
                placeholder: translate("Your Last Name"),
                required: true,
                maxLength: 40,
            },
        },
        email: {
            value: "",
            validators: ["required", "email"],
            attrs: {
                type: "email",
                name: "email",
                id: "createAccount_email",
                label: translate("Email Address"),
                placeholder: translate("Your Email"),
                required: true,
                maxLength: maxLengthAttr.email,
            },
        },
        userName: {
            value: "",
            validators: (state, options) => {
                return !options?.accountSettings?.useEmailAsUserName ? ["required"] : [];
            },
            attrs: {
                id: "createAccount_userName",
                name: "userName",
                label: translate("User Name"),
                placeholder: translate("Your User Name"),
                required: true,
            },
        },
        phone: {
            value: "",
            validators: ["required", "phone"],
            attrs: {
                id: "createAccount_phoneNumber",
                name: "phone",
                label: translate("Phone Number"),
                placeholder: "( ___ ) ___ - ____",
                required: true,
                maxLength: 14,
            },
        },
        password: {
            value: "",
            validators: ["required", "password"],
            validatorsOptions: state => {
                return { confirmPassword: state.fields.confirmPassword.value };
            },
            attrs: {
                id: "createAccount_password",
                name: "password",
                label: translate("Password"),
                placeholder: translate("Password"),
                required: true,
            },
        },
        confirmPassword: {
            value: "",
            validators: ["required", "confirmPassword"],
            validatorsOptions: state => {
                return { password: state.fields.password.value };
            },
            attrs: {
                id: "createAccount_confirmPassword",
                name: "confirmPassword",
                label: translate("Confirm Password"),
                placeholder: translate("Confirm Password"),
                required: true,
            },
        },
        companyName: {
            value: "",
            validators: ["required"],
            attrs: {
                id: "createAccount_companyName",
                name: "companyName",
                label: translate("Company Name"),
                placeholder: translate("Your Company Name"),
                required: true,
            },
        },
        address1: {
            value: "",
            validators: ["required"],
            attrs: {
                id: "createAccount_address1",
                name: "address1",
                label: translate("Address1"),
                placeholder: translate("Your First Address"),
                required: true,
            },
        },
        address2: {
            value: "",
            validators: [],
            attrs: {
                id: "createAccount_address2",
                name: "address2",
                label: translate("Address2"),
                placeholder: translate("Your Second Address"),
            },
        },
        country: {
            value: "",
            validators: ["required"],           
            attrs: {
                id: "createAccount_country",
                name: "country",
                label: translate("Country"),
                required: true,
            },
        },
        state: {
            value: "",
            validators: ["required"],
            attrs: {
                id: "createAccount_state",
                name: "state",
                label: translate("State"),
                required: true,
            },
        },
        city: {
            value: "",
            validators: ["required"],
            attrs: {
                id: "createAccount_city",
                name: "city",
                label: translate("City"),
                placeholder: translate("Your City"),
                required: true,
            },
        },
        zip: {
            value: "",
            validators: ["required", "zipCode"],
            validatorsOptions: state => {
                return { country: state.fields.country.value };
            },
            attrs: {
                id: "createAccount_zip",
                name: "zip",
                label: translate("Zip/Postal Code"),
                placeholder: translate("Zip Code"),
                required: true,
            },
        },
        accountNumber: {
            value: "",
            validators: ["required"],
            attrs: {
                id: "createAccount_accountNumber",
                name: "accountNumber",
                label: translate("Account Number"),
                placeholder: translate("Your Account Number"),
                required: true,
            },
        },
        notes: {
            value: "",
            validators: [],
            attrs: {
                id: "createAccount_notes",
                name: "notes",
                label: translate("Notes"),
                placeholder: translate("Notes"),
                maxLength: 250,
            },
        },
        creditApplicationFile: {
            value: null,
            attrs: {
                name: "creditApplicationFile",
            },
        },
    },
};
